<template>
  <main>
    <Loader :loader="formLoader" label="Carregando..." loaderType="progress" />

    <form v-if="!formLoader" @submit.prevent="saveMaterials()">
      <section class="bg-h-primary-50 border py-2">
        <div class="container mx-auto">
          <BreadcrumbComponent :home="home" :items="items" />
        </div>
      </section>
      <section class="mt-[64px]">
        <div class="w-full container mx-auto flex justify-between">
          <h4>
            {{
              $t("labels.generic", {
                pt: `Plano ${getMaterialTypePT(lessonPlan.name)}`,
                en: `Plan ${lessonPlan.name}`,
              })
            }}
          </h4>
          <div class="space-x-2">
            <Button class="btn-outline" @click="confirmModal()">
              {{
                $t("labels.generic", {
                  pt: `Cancelar`,
                  en: `Cancel`,
                })
              }}
            </Button>
            <Button class="btn-primary" type="submit">
              {{
                $t("labels.generic", {
                  pt: `Salvar`,
                  en: `Save`,
                })
              }}
            </Button>
          </div>
        </div>
      </section>
      <section class="container mx-auto mt-8">
        <InfiniteScroll @onScroll="scrollMaterials">
          <template #header>
            <div
              class="grid grid-cols-12 items-center overflow-auto px-5 py-3 gap-5"
            >
              <label class="col-span-12 md:col-span-2 text-xl font-bold">
                {{
                  $t("labels.generic", {
                    pt: `Selecionar objetos`,
                    en: `Select objects`,
                  })
                }}
              </label>
              <div class="col-span-6 md:col-span-3">
                <span class="p-input-icon-right w-full">
                  <i
                    class="pi pi-search text-h-primary-500 cursor-pointer"
                    @click="onSearch()"
                  />
                  <InputText
                    v-model="searchWord"
                    :placeholder="
                      $t('labels.generic', { pt: 'Pesquisar', en: 'Search' })
                    "
                    class="w-full"
                    @keydown.enter.prevent="onSearch()"
                  />
                </span>
              </div>

              <div class="col-span-6 md:col-span-3">
                <Dropdown
                  v-model="selected"
                  :options="filters"
                  optionLabel="name"
                  class="w-full"
                  @change="onFilter()"
                />
              </div>
              <div class="col-span-12 md:col-span-4 flex justify-end">
                <div class="flex justify-center gap-3">
                  <p
                    :class="
                      selected.active !== 0 || searchWord !== ''
                        ? 'text-gray-300'
                        : ''
                    "
                  >
                    {{
                      $t("labels.generic", {
                        pt: "Selecionar tudo",
                        en: "Select all",
                      })
                    }}
                  </p>
                  <Checkbox
                    v-model="selectAll"
                    @click="selectAllMaterials()"
                    :binary="true"
                    :disabled="selected.active !== 0 || searchWord !== ''"
                  />
                </div>
              </div>
            </div>
          </template>
          <template #content>
            <div
              id="content"
              v-for="(item, i) in materials"
              :key="i"
              :class="`h-24 border-2 border-gray-100 p-4 flex flex-row items-center justify-between`"
            >
              <div class="flex flex-row items-center space-x-4">
                <v-icon
                  :name="getIcon(item.file?.mimetype)"
                  class="text-h-primary-500"
                  :scale="1.5"
                />
                <div class="">
                  <p class="font-[800]">{{ item.name }}</p>
                  <div class="flex flex-row space-x-2">
                    <p>
                      {{
                        $t("labels.generic", {
                          pt: `Classificação: ${getLearningGroupTypePT(
                            item.learningGroupType
                          )} |`,
                          en: `Classification: ${item.learningGroupType} |`,
                        })
                      }}
                    </p>
                    <TagMaterials :material="item" />
                  </div>
                </div>
              </div>
              <Checkbox
                v-model="item.checkbox"
                :inputId="item.name"
                :value="item.id"
                :binary="true"
                @click="addMaterial(item)"
              />
            </div>
          </template>
        </InfiniteScroll>
      </section>
    </form>
    <ConfirmDialog :style="{ width: '80vw', maxWidth: '700px' }" />
  </main>
</template>

<script lang="ts">
import { Chapter } from "@/models/chapter.model";
import { LearningObjective } from "@/models/learning-objective.model";
import { LessonPlan } from "@/models/lessonPlan.model";
import { MaterialLearningGroupType } from "@/models/material.model";
import { QueryParams } from "@/models/query-params.model";
import { LessonPlanRest } from "@/services/rest/lessonPlan.rest";
import { GoToRoute } from "@/utils/go-to.util";
import { MaterialsUtils } from "@/utils/materials.util";
import { getItemLocalStorage } from "@/utils/save-storage.util";
import { debounceTime, last, take } from "rxjs";
import { defineComponent } from "vue";
import { LessonPlanService } from "../../LessonPlan/lessonPlan.service";
import { MaterialsService } from "../../Materials/materials.service";
import { ObjectiveService } from "../../objective.service";

export default defineComponent({
  name: "courseContent",
  computed: {
    serviceMaterial(): MaterialsService {
      return new MaterialsService();
    },
    serviceObjective(): ObjectiveService {
      return new ObjectiveService();
    },
    serviceLessonPlan(): LessonPlanService {
      return new LessonPlanService();
    },
    materialsUtils(): MaterialsUtils {
      return new MaterialsUtils();
    },
    idLessonPlan(): string {
      return this.$route.params.lessonPlanId as string;
    },
    idObjective(): string {
      return this.$route.params.objectiveId as string;
    },
    lessonPlanRest(): LessonPlanRest {
      return new LessonPlanRest();
    },
    go(): GoToRoute {
      return new GoToRoute();
    },
  },
  data() {
    return {
      materials: [] as any,
      params: new QueryParams(0, 15),
      formLoader: true as boolean,
      loadingSave: false as boolean,
      blockScroll: true as boolean,
      lessonPlan: {} as LessonPlan,
      learningObjective: {} as LearningObjective,
      filters: [
        {
          name: this.$t("labels.generic", { pt: "Todos", en: "All" }),
          query: "",
          active: 0,
        },
        {
          name: this.$t("objects.type.motivation"),
          query: MaterialLearningGroupType.MOTIVATION,
          active: 1,
        },
        {
          name: this.$t("objects.type.intro"),
          query: MaterialLearningGroupType.INTRODUCTION,
          active: 2,
        },
        {
          name: this.$t("objects.type.dev"),
          query: MaterialLearningGroupType.DEVELOPMENT,
          active: 3,
        },
        {
          name: this.$t("objects.type.closure"),
          query: MaterialLearningGroupType.CLOSURE,
          active: 4,
        },
      ],
      selected: {
        name: this.$t("labels.generic", { pt: "Todos", en: "All" }),
        query: "",
        active: 0,
      } as any,
      selectAll: null as any,
      searchWord: "" as string,
      classificationOptions: [] as object[],
      classificationMaterials: [
        MaterialLearningGroupType.INTRODUCTION,
        MaterialLearningGroupType.MOTIVATION,
        MaterialLearningGroupType.DEVELOPMENT,
        MaterialLearningGroupType.CLOSURE,
      ],
      home: {
        label: this.getChapterName().name,
        command: () => this.$router.go(-1),
      },
      items: [
        {
          label: this.$t("labels.generic", {
            pt: "Selecionar objetos",
            en: "Select objects",
          }),
        },
      ],
    };
  },
  mounted() {
    this.getLessonPlanById();
    for (var i = 0; i < this.classificationMaterials.length; i++) {
      let data = {
        name: this.$t("labels.generic", {
          pt: this.getLearningGroupTypePT(this.classificationMaterials[i]),
          en: this.classificationMaterials[i],
        }),
        code: i,
      };
      this.classificationOptions.push(data);
    }
  },

  methods: {
    setMaterialsPage(queryParams: QueryParams) {
      this.serviceObjective.materials
        .pipe(take(2), last(), debounceTime(200))
        .subscribe({
          next: (response) => {
            if (response && response.content && response.content.length !== 0) {
              this.formLoader = false;
              this.params.total = response.total;
              this.params.size = response.size;
              const materials = this.addCheckbox(response.content);
              this.materials.push(...materials);
              this.blockScroll = false;
            } else {
              this.blockScroll = true;
            }
          },
          complete: () => {
            this.formLoader = false;
          },
        });
      this.serviceObjective.getMaterialsFromObjective(
        this.idObjective,
        queryParams
      );
    },
    getAllMaterials(): void {
      const page = this.params.page;
      const size = this.params.size;
      const groupType = this.params.groupType ?? undefined;
      const name = this.params.name ?? undefined;
      const params = new QueryParams(page, size);
      params.groupType = groupType;
      params.name = name;
      this.setMaterialsPage(params);
    },
    onFilter(): void {
      this.materials.length = 0;
      (this.$refs.infiniteScroll as HTMLDivElement)?.scrollTo(0, 0);
      this.params.groupType = this.selected.query;
      this.params.page = 0;
      setTimeout(() => {
        this.getAllMaterials();
      }, 150);
    },
    onSearch(): void {
      this.materials.length = 0;
      (this.$refs.infiniteScroll as HTMLDivElement)?.scrollTo(0, 0);
      this.params.name = this.searchWord;
      this.params.page = 0;
      setTimeout(() => {
        this.getAllMaterials();
      }, 150);
    },
    scrollMaterials(event: HTMLDivElement): void {
      const { scrollTop, clientHeight, scrollHeight } = event;
      if (scrollTop + clientHeight >= scrollHeight) {
        if (!this.blockScroll) {
          this.params.page++;
          this.getAllMaterials();
        }
      }
    },
    getLessonPlanById(): void {
      this.serviceLessonPlan.lessonPlan.pipe(take(2), last()).subscribe({
        next: (response) => {
          this.lessonPlan = response;
          this.getAllMaterials();
        },
      });
      this.serviceLessonPlan.getLessonPlanById(
        this.idObjective,
        this.idLessonPlan
      );
    },
    getMaterialTypePT(lessonPlanName: string): string {
      return this.materialsUtils.getMaterialTypePT(lessonPlanName);
    },
    getIcon(icon: string): string {
      return this.materialsUtils.getIcon(icon);
    },
    getLearningGroupTypePT(learningGroup: string): string {
      return this.materialsUtils.getLearningGroupTypePT(learningGroup);
    },
    addCheckbox(materials: any): any {
      materials.map((material) => {
        let index = this.lessonPlan.materials.findIndex(
          (lessonMaterial) => material.id == lessonMaterial.id
        );
        if (index != -1) {
          material.checkbox = true;
        } else {
          material.checkbox = false;
        }
      });
      return materials;
    },
    addMaterial(material: any): void {
      let index = this.lessonPlan.materials.findIndex(
        (lessonMaterial) => material.id == lessonMaterial.id
      );
      if (index != -1) {
        this.lessonPlan.materials.splice(index, 1);
      } else {
        this.lessonPlan.materials.push(material);
      }
    },
    selectAllMaterials(): void {
      this.materials.map((material) => {
        this.lessonPlan.materials.push(material);
        return (material.checkbox = !this.selectAll);
      });
    },
    saveMaterials(): void {
      this.formLoader = true;
      const data = {
        name: this.lessonPlan.name,
        materials:
          this.selectAll == null || this.selectAll == true
            ? this.lessonPlan.materials.map((e) => e.id)
            : [],
      };
      this.lessonPlanRest
        .editMaterialsInLessonPlan(
          this.lessonPlan.learningObjective,
          this.lessonPlan.id,
          data
        )
        .subscribe({
          next: () => {
            this.redirectToPage();
          },
          error: () => {
            this.formLoader = false;
          },
        });
    },
    redirectToPage(): void {
      let routePageParams = {
        id: this.lessonPlan?.learningObjective,
        courseId: this.$route.params.courseId,
        chapterId: this.$route.params.chapterId,
        tab: 2,
      };
      this.go.redirectToPage("formObjective", routePageParams);
    },
    getChapterName(): Chapter {
      return JSON.parse(getItemLocalStorage("objective") as string);
    },
    confirmModal(event?: any): void {
      this.$confirm.require({
        message: this.$t("labels.generic", {
          pt: `Os dados não salvos serão perdidos! Tem certeza que desejar sair?`,
          en: `Unsaved data will be lost! Are you sure you want to quit?`,
        }),
        header: this.$t("labels.generic", {
          pt: "Sair da tela",
          en: "Exit Screen",
        }),
        acceptLabel: "Sim",
        rejectLabel: "Não",
        rejectClass: "btn-outline",
        acceptClass: "btn-primary",
        accept: () => {
          this.redirectToPage();
        },
      });
    },
  },
});
</script>
